import { useIsMutating, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { proBuilderKeys } from '~/lib/query-keys';
import {
  PRO_BUILDER_APP_PATH,
  type ProBuilderBoundApp,
  type ProBuilderConnection,
  type ProBuilderConsentMode,
  type ProBuilderMode,
  type ProBuilderProject,
  type ProBuilderStatus,
} from '~/hooks/queries/use-pro-builder';
import { shouldRestartTerminal } from '~/features/pro-builder/pro-builder-app-rules';
import { useProBuilderPublishStore } from '~/features/pro-builder/pro-builder-publish-store';
import { useProBuilderPickerStore } from '~/features/pro-builder/pro-builder-picker-store';
import { useProBuilderSessionStore } from '~/features/pro-builder/pro-builder-session-store';

/**
 * Body of `PUT /api/pro-builder/connection`. Every field is optional:
 * `apiKey` / `bedrock` as a value sets, as `null` removes, absent leaves
 * unchanged. Secrets go out only here and are never read back.
 */
export interface ProBuilderConnectionInput {
  mode?: ProBuilderMode;
  apiKey?: string | null;
  bedrock?: {
    region: string;
    accessKeyId: string;
    secretAccessKey: string;
    sessionToken?: string;
  } | null;
}

/**
 * `onSaved` runs from the MUTATION-level `onSuccess`, which TanStack fires even
 * when the component that called `mutate()` has unmounted — unlike a per-call
 * `mutate(…, { onSuccess })`, which is dropped on unmount (Settings closed
 * while the save is still in flight). It receives the body that was saved, so
 * the caller can tell which credentials the save set or removed.
 */
export function useSaveProBuilderConnection({ onSaved }: { onSaved?: (input: ProBuilderConnectionInput) => void } = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProBuilderConnectionInput) =>
      api.put<{ connection: ProBuilderConnection }>('/api/pro-builder/connection', input),
    onSuccess: (_out, input) => {
      onSaved?.(input);
      return qc.invalidateQueries({ queryKey: proBuilderKeys.all });
    },
  });
}

/**
 * FRONT-3228: save the auto-compact threshold (`null` = Claude Code's default).
 * `scope` makes quick successive commits (arrow keys) run one after another, so
 * the server's last write is the last value chosen and responses land in order;
 * each response is written straight into the status read, so the slider and the
 * restart row follow the saved value without a refetch racing a newer save.
 */
export function autoCompactMutationOptions(qc: QueryClient) {
  return {
    scope: { id: 'pro-builder-auto-compact' },
    // A status read already in flight was issued before this save and would land
    // after the response below, overwriting the newer value with the older one.
    // Cancelled here, not in `onMutate`: `onMutate` runs at once even while
    // `scope` still holds this save behind an earlier one, so a read started in
    // between would survive.
    mutationFn: async (autoCompactPct: number | null) => {
      await qc.cancelQueries({ queryKey: proBuilderKeys.status() });
      return api.put<{ autoCompactPct: number | null }>('/api/pro-builder/auto-compact', { autoCompactPct });
    },
    onSuccess: (out: { autoCompactPct: number | null }) => {
      qc.setQueryData<ProBuilderStatus>(proBuilderKeys.status(), (old) => (old ? { ...old, autoCompactPct: out.autoCompactPct } : old));
    },
  };
}

export function useSaveProBuilderAutoCompact() {
  return useMutation(autoCompactMutationOptions(useQueryClient()));
}

/**
 * FRONT-3040: tell the server the person has looked at the Pro Builder
 * session's current state (the board row then turns grey). Invalidates the
 * session read so the board follows. Callers must never `reset()` this while
 * it may be pending.
 */
export function useMarkProBuilderSeen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<void>('/api/pro-builder/session/seen', {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: proBuilderKeys.session() }),
  });
}

/**
 * FRONT-3108: answer the consent card a Claude Code terminal waits on. Never
 * settled optimistically: the card goes away only when the refetch no longer
 * has it. Settling (success or error — a 404/409 means it is gone or already
 * answered) re-reads the card AND the session view that flags the task-list row.
 */
export function useAnswerProBuilderConsent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ batchId, approved, mode }: { batchId: string; approved: boolean; mode?: ProBuilderConsentMode }) =>
      api.post<{ ok: true }>(`/api/pro-builder/consent/${encodeURIComponent(batchId)}`, { approved, ...(mode ? { mode } : {}) }),
    // FRONT-3230: an answer carrying a "Next time" choice re-reads the saved setting, which the server keeps only with an accepted answer.
    onSettled: (_out, _err, vars) =>
      Promise.all([
        qc.invalidateQueries({ queryKey: proBuilderKeys.consent() }),
        qc.invalidateQueries({ queryKey: proBuilderKeys.session() }),
        ...(vars.mode ? [qc.invalidateQueries({ queryKey: proBuilderKeys.status() })] : []),
      ]),
  });
}

/**
 * FRONT-3230: save how much the person wants to be asked (the gear menu).
 * Same shape as the compaction threshold's save: one scope so two quick
 * picks land in order, a status read in flight cancelled first, and the
 * answer written straight into the status read.
 */
export function consentModeMutationOptions(qc: QueryClient) {
  return {
    scope: { id: 'pro-builder-consent-mode' },
    mutationFn: async (consentMode: ProBuilderConsentMode) => {
      await qc.cancelQueries({ queryKey: proBuilderKeys.status() });
      return api.put<{ consentMode: ProBuilderConsentMode }>('/api/pro-builder/consent-mode', { consentMode });
    },
    onSuccess: (out: { consentMode: ProBuilderConsentMode }) => {
      qc.setQueryData<ProBuilderStatus>(proBuilderKeys.status(), (old) => (old ? { ...old, consentMode: out.consentMode } : old));
    },
  };
}

export function useSaveProBuilderConsentMode() {
  return useMutation(consentModeMutationOptions(useQueryClient()));
}

/** Admin only: the one org-level switch for the whole feature. */
export function useSetProBuilderOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      api.put<{ orgEnabled: boolean }>('/api/pro-builder/org', { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: proBuilderKeys.all }),
  });
}

/** Body of `PUT /api/pro-builder/app`: exactly one key. */
export type ProBuilderBindInput = { folder: string } | { slug: string } | { name: string };

/**
 * `PUT /api/pro-builder/app` with `{ name }` is NOT idempotent: a retried 5xx
 * after the folder was made would create `foo-2`, `foo-3`. The query client's
 * default mutation retry (3 tries on a 5xx or network failure) must not apply.
 */
export const BIND_MUTATION_OPTIONS = { retry: false } as const;

/** Fixed key so any component can ask whether a bind is in flight anywhere (`useProBuilderSwitching`). */
export const BIND_MUTATION_KEY = ['pro-builder', 'bind-app'] as const;

/** Whether a `PUT /api/pro-builder/app` is pending in ANY component — the header's "switching" busy reason. */
export function useProBuilderSwitching(): boolean {
  return useIsMutating({ mutationKey: BIND_MUTATION_KEY }) > 0;
}

/**
 * FRONT-3077: bind the window to one app. Success: store the answer as the app
 * read, refresh the project list, leave the "New app" view, and restart the
 * terminal session only when the server says it ended a running shell. All of
 * it runs from the MUTATION-level `onSuccess` so it still happens when the
 * caller (a picker row, the header's app switcher, the New app view, a card)
 * has unmounted. Callers must never `reset()` this while pending.
 */
export function useBindProBuilderApp() {
  const qc = useQueryClient();
  return useMutation({
    ...BIND_MUTATION_OPTIONS,
    mutationKey: BIND_MUTATION_KEY,
    mutationFn: (input: ProBuilderBindInput) =>
      api.put<{ folder: string; project: ProBuilderProject; restarted: boolean }>(PRO_BUILDER_APP_PATH, input),
    onSuccess: (out) => {
      qc.setQueryData<ProBuilderBoundApp>(proBuilderKeys.app(), { folder: out.folder, project: out.project });
      void qc.invalidateQueries({ queryKey: proBuilderKeys.projects() });
      useProBuilderPickerStore.getState().setCreating(false);
      // The bound folder changed: a details marker left for the previous app must never pop its dialog open for this one later.
      useProBuilderPublishStore.getState().closeDetails();
      if (shouldRestartTerminal(out.restarted)) {
        useProBuilderSessionStore.getState().dispatch({ type: 'restart' });
      }
    },
  });
}
